import { datadogRum } from '@datadog/browser-rum';
import Loading from "components/center-loading";
import { ThemeProvider } from "context/themeContext";
import enGB from "date-fns/locale/en-GB";
import enUS from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import ptBR from "date-fns/locale/pt-BR";
import { useNewNpsScript } from "hooks/useNewNpsScript";
import momentEnGb from "locales/moment/moment-en-GB";
import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import moment from "moment";
import ErrorPage from "pages/404";
import PropTypes from 'prop-types';
import { loadMonitoringApplications } from "providers/monitoring";
import { useEffect, useState } from "react";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import intl from "react-intl-universal";
import { getAuthenticationProvider, getZone } from "utils/config-environment";
import SessionUser from 'utils/user-storage';
import { getDefaultLocale, getLocaleForLanguageSheet } from "utils/userLanguage";
import "./app.css";
import { storageDailyDeliveryFeatureFlagAsync } from "utils/feature-flags";
import AppRoutes from "./config/routes";
import { UserProvider } from "./context/userContext";
import locales from "./locales";
import momentEnUs from "./locales/moment/moment-en-US";
import momentEsEs from "./locales/moment/moment-es-ES";
import momentPtBr from "./locales/moment/moment-pt-BR";
import { default as userRoles } from './models/usuarios/access-profile-type-nps';
import {
  getTokenAuthorization,
  getUser,
  validateErrorSSO,
} from "./providers/authService";
import { initGeneralSettings } from "./services/general-settings/general-settings";
import { getDocumentTypes } from "./utils/document-types";

const App = ({ accountInfo, error }) => {
  const [initDone, setInitDone] = useState(false);

  const initialConfigurations = async () => {
    window.document.title = "Ontime MRP";
    loadMonitoringApplications();
    const user = await validUser();
    await loadLocales(user);
    if (user) {
      await loadUserInfos();
      await loadDocumentTypes();
      await initGeneralSettings();
      await storageDailyDeliveryFeatureFlagAsync();
      setInitDone(true);
    } else {
      setTimeout(() => setInitDone(true), 3000);
    }
  };

  useNewNpsScript(initDone? accountInfo?.jwtIdToken : "");

  const loadUserInfos = async () => {
    setUserForDatadogRum();
    await loadTokenAuthorization();
  };

  const setUserForDatadogRum = () => {
    const user = SessionUser.get();
    datadogRum.setUser({
      id: user.Id,
      role: userRoles[user.PerfilAcesso] || "Undefined",
      zone: getZone() || "Undefined",
    });
  };

  const loadLocales = async (user) => {
    const language = getLocaleForLanguageSheet(user.LocaleCode);
    await intl.init({
      currentLocale: language,
      locales,
    });

    setUpLocalizationLibsSettings(user.LocaleCode);
  };

  const validUser = () => {
    return getUser();
  };

  const setUpLocalizationLibsSettings = (language) => {
    registerLocale("pt-BR", ptBR);
    registerLocale("es-ES", es);
    registerLocale("en-US", enUS);
    registerLocale("en-GB", enGB);

    const _defaultLocale = getDefaultLocale(language);
    setDefaultLocale(_defaultLocale);

    switch (language) {
      case "pt-BR":
        moment.updateLocale("pt-br", momentPtBr);
        break;
      case "es-ES":
      case "es-BO":
      case "es-MX":
        moment.updateLocale("es", momentEsEs);
        moment.locale("es");
        break;
      case "en-GB":
        moment.updateLocale("en-gb", momentEnGb);
        break;
      case "en-US":
      default:
        moment.updateLocale("en", momentEnUs);
        break;
    }
  };

  const loadTokenAuthorization = async () => {
    const newToken = await getTokenAuthorization();
    localStorage.setItem("ValidationToken", newToken.Token);
  };

  const isAuthentication = () =>
    accountInfo &&
    accountInfo.account &&
    accountInfo.account.idToken &&
    accountInfo.account.idToken.emails;

  const loadDocumentTypes = async () => {
    const documentTypes = await getDocumentTypes();
    localStorage.setItem("DocumentTypes", JSON.stringify(documentTypes));
  };

  useEffect(() => {
    if (isAuthentication()) {
      initialConfigurations();
    }
  }, []);

  useEffect(() => {
    if (validateErrorSSO(error)) {
      window.location.href = "/";
    }
  });

  if (initDone && accountInfo && getAuthenticationProvider() === "B2C") {
    return (
      <ThemeProvider>
        <MuiThemeProvider>
          <UserProvider>
            <AppRoutes />
          </UserProvider>
        </MuiThemeProvider>
      </ThemeProvider>
    );
  }

  if (initDone && getAuthenticationProvider() !== "B2C") {
    return (
      <MuiThemeProvider>
        <ErrorPage />
      </MuiThemeProvider>
    );
  }

  return (
    <MuiThemeProvider>
      <Loading isLoading={true} fullHeight />
    </MuiThemeProvider>
  );
};

App.propTypes = {
  accountInfo: PropTypes.shape({
    account: PropTypes.shape({
      idToken: PropTypes.shape({
        emails: PropTypes.string,
        idp_access_token: PropTypes.string,
      }),
    }),
    jwtIdToken: PropTypes.string,
  }),
  error: PropTypes.object,
};

export default App;
